import { IconNames } from '@blueprintjs/icons';
import { BenefitTypes } from '@strata/constants';
import { ActionButtonText, Button } from '@strata/react-buttons';
import { DateInputFormik } from '@strata/react-date-time';
import { WarningDialog } from '@strata/react-dialog';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { Icon } from '@strata/react-status-icons';
import dayjs from 'dayjs';
import { FieldArray, useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { ListPageHeader } from 'components/ListPageHeader';
import { handleError } from 'services/ErrorManagement';
import {
    getRouteSequencing,
    getRulePlacementOptions,
    SequenceOptions,
} from 'services/RouteRuleHelpers';

const sequenceOptions = Object.values(SequenceOptions).map((sequence) => ({
    name: sequence,
    tooltip:
        sequence === SequenceOptions.REPLACE
            ? "The replaced route will be set to expire one day before this route's start date."
            : undefined,
}));

const payerSegmentPropType = PropTypes.shape({
    distinct_id: PropTypes.string.isRequired,
    payer_id: PropTypes.string,
    segment_id: PropTypes.string,
    sequence: PropTypes.oneOf(Object.values(SequenceOptions)),
    rule_placement: PropTypes.string,
    start_date: PropTypes.string,
    end_date: PropTypes.string,
    existing: PropTypes.shape({ route_rule_id: PropTypes.string.isRequired }),
});

const PayerSegmentSequencing = ({
    arrayHelpers,
    index,
    segment,
    segments,
    benefit = undefined,
    payerSegmentOptions = undefined,
    configuredSegmentRoutes = undefined,
    isGlobalRoute,
    isCurrentSegment,
    minStartDate = undefined,
    onRemoveClick = undefined,
}) => {
    // The page loads the routes of every segment the rule is already configured in
    const configuredRoutes = configuredSegmentRoutes?.[segment.segment_id];

    const [addedSegmentRoutes, setAddedSegmentRoutes] = useState();
    useEffect(() => {
        if (configuredRoutes || !segment.payer_id || !segment.segment_id || !benefit) {
            return;
        }
        void (async () => {
            try {
                setAddedSegmentRoutes(
                    await getRulePlacementOptions(segment.payer_id, segment.segment_id, benefit),
                );
            } catch (err) {
                handleError('An error occurred loading route rules.', err);
            }
        })();
    }, [configuredRoutes, segment.payer_id, segment.segment_id, benefit]);

    const routes = configuredRoutes ?? addedSegmentRoutes;
    const rulePlacementOptions = routes
        ?.filter((route) => route.value !== segment.existing?.route_rule_id)
        .map((route) => ({
            ...route,
            disabled:
                {
                    [SequenceOptions.BEFORE]: route.disabledBefore,
                    [SequenceOptions.REPLACE]: route.disabledReplace,
                }[segment.sequence] ?? route.disabledAfter,
        }));

    const inputs = (
        <>
            <div className="sds_form-page__input-group">
                {isGlobalRoute && (
                    <SuggestSingleSelectFormik
                        id={`${arrayHelpers.name}.${index}.segment_id`}
                        label="Payer Segment"
                        required
                        // A segment already configured must be removed rather than changed
                        disabled={isCurrentSegment || !!segment.existing}
                        isLoading={!payerSegmentOptions}
                        values={payerSegmentOptions?.filter(
                            (option) =>
                                option.value === segment.segment_id ||
                                !segments.some(
                                    ({ segment_id: selected }) => selected === option.value,
                                ),
                        )}
                        selectedItem={payerSegmentOptions?.find(
                            (option) => option.value === segment.segment_id,
                        )}
                        onChange={(newValue) =>
                            arrayHelpers.replace(index, {
                                distinct_id: segment.distinct_id,
                                payer_id: newValue?.payer_id,
                                segment_id: newValue?.value,
                                start_date: segment.start_date,
                                end_date: segment.end_date,
                            })
                        }
                    />
                )}
                <DateInputFormik
                    id={`${arrayHelpers.name}.${index}.start_date`}
                    label="Start Date"
                    required
                    minDate={minStartDate}
                />
                <DateInputFormik id={`${arrayHelpers.name}.${index}.end_date`} label="End Date" />
            </div>
            <div className="sds_form-page__input-group">
                <SuggestSingleSelectFormik
                    id={`${arrayHelpers.name}.${index}.sequence`}
                    label="Sequence"
                    required
                    values={sequenceOptions}
                    selectedItem={sequenceOptions.find(
                        (option) => option.name === segment.sequence,
                    )}
                    onChange={(newValue) =>
                        arrayHelpers.replace(index, {
                            ...segment,
                            sequence: newValue?.name,
                            rule_placement: undefined,
                            before_continuity: undefined,
                            route_sequence: undefined,
                        })
                    }
                />
                <SuggestSingleSelectFormik
                    id={`${arrayHelpers.name}.${index}.rule_placement`}
                    label="Rule Placement"
                    required
                    values={rulePlacementOptions}
                    isLoading={!rulePlacementOptions}
                    selectedItem={rulePlacementOptions?.find(
                        (route) => route.value === segment.rule_placement,
                    )}
                    onChange={(newValue) =>
                        arrayHelpers.replace(index, {
                            ...segment,
                            rule_placement: newValue?.value,
                            ...getRouteSequencing({
                                routes: rulePlacementOptions,
                                sequence: segment.sequence,
                                rulePlacement: newValue?.value,
                                existing: segment.existing,
                            }),
                        })
                    }
                />
                {onRemoveClick && (
                    <Icon
                        className="margin__top--large"
                        icon={IconNames.CROSS}
                        intent={Icon.Intent.SECONDARY}
                        title="Remove segment"
                        onClick={onRemoveClick}
                    />
                )}
            </div>
        </>
    );

    if (!isGlobalRoute) {
        return inputs;
    }

    return <div className="sds_card-array__card sds_payers-and-sequencing__card">{inputs}</div>;
};

PayerSegmentSequencing.propTypes = {
    arrayHelpers: PropTypes.shape({
        name: PropTypes.string.isRequired,
        replace: PropTypes.func.isRequired,
    }).isRequired,
    index: PropTypes.number.isRequired,
    segment: payerSegmentPropType.isRequired,
    segments: PropTypes.arrayOf(payerSegmentPropType).isRequired,
    benefit: PropTypes.oneOf(Object.values(BenefitTypes)),
    payerSegmentOptions: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string })),
    configuredSegmentRoutes: PropTypes.objectOf(
        PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string })),
    ),
    isGlobalRoute: PropTypes.bool.isRequired,
    isCurrentSegment: PropTypes.bool.isRequired,
    minStartDate: PropTypes.instanceOf(Date),
    onRemoveClick: PropTypes.func,
};

/**
 * Sequencing inputs for the segment a route rule is configured in. Global routes can be sequenced
 * into additional payer segments, each with their own placement and effective dates.
 */
export const PayersAndSequencing = ({
    benefit = undefined,
    payerSegmentOptions = undefined,
    configuredSegmentRoutes = undefined,
    currentSegmentId = undefined,
}) => {
    const formik = useFormikContext();
    const isGlobalRoute = formik.values.global_route;

    const [indexToRemove, setIndexToRemove] = useState();

    const today = dayjs().startOf('day');
    // A placement that already took effect keeps its past start date; any other starts today at the earliest
    const getMinStartDate = (distinctId) => {
        const savedStartDate = formik.initialValues.payer_segments?.find(
            (segment) => segment.distinct_id === distinctId,
        )?.start_date;
        return savedStartDate && today.isAfter(savedStartDate, 'day') ? undefined : today.toDate();
    };

    const segmentIdToRemove = formik.values.payer_segments[indexToRemove]?.segment_id;

    return (
        <FieldArray name="payer_segments">
            {(arrayHelpers) => (
                <>
                    {isGlobalRoute && <ListPageHeader pageTitle="Payers And Sequencing" />}
                    {formik.values[arrayHelpers.name].map((segment, index, segments) => {
                        // The segment the rule is being edited in cannot be changed or removed
                        const isCurrentSegment =
                            !!currentSegmentId && segment.segment_id === currentSegmentId;

                        return (
                            <PayerSegmentSequencing
                                key={segment.distinct_id}
                                arrayHelpers={arrayHelpers}
                                index={index}
                                segment={segment}
                                segments={segments}
                                benefit={benefit}
                                payerSegmentOptions={payerSegmentOptions}
                                configuredSegmentRoutes={configuredSegmentRoutes}
                                isGlobalRoute={isGlobalRoute}
                                isCurrentSegment={isCurrentSegment}
                                minStartDate={getMinStartDate(segment.distinct_id)}
                                onRemoveClick={
                                    isCurrentSegment ? undefined : () => setIndexToRemove(index)
                                }
                            />
                        );
                    })}
                    {isGlobalRoute && (
                        <ActionButtonText
                            className="margin__bottom--large"
                            label="Add Another Segment"
                            leftIcon={IconNames.ADD}
                            onClick={() => arrayHelpers.push({ distinct_id: uuidv4() })}
                        />
                    )}
                    <WarningDialog
                        dialogTitle="Confirm Remove"
                        width={600}
                        isOpen={indexToRemove != null}
                        primaryButtonLabel="Yes, Remove Segment"
                        primaryButtonIntent={Button.Intent.DANGER}
                        onPrimaryButtonClick={() => {
                            arrayHelpers.remove(indexToRemove);
                            setIndexToRemove();
                        }}
                        secondaryButtonLabel="No, Cancel"
                        onSecondaryButtonClick={() => setIndexToRemove()}
                        dialogContent={
                            segmentIdToRemove
                                ? `This will delete the global route from ${segmentIdToRemove}'s route rules.`
                                : 'Are you sure you want to remove this payer segment from the route rule?'
                        }
                    />
                </>
            )}
        </FieldArray>
    );
};

PayersAndSequencing.propTypes = {
    benefit: PropTypes.oneOf(Object.values(BenefitTypes)),
    payerSegmentOptions: PropTypes.arrayOf(
        PropTypes.shape({ value: PropTypes.string, name: PropTypes.string }),
    ),
    configuredSegmentRoutes: PropTypes.objectOf(
        PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string })),
    ),
    currentSegmentId: PropTypes.string,
};
